import React, { useCallback, useEffect, useRef, useReducer, useMemo } from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Paper, Grid, Box, Divider } from '@material-ui/core';
import moment from 'moment';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';

import CalendarioSemanal from '../CalendarioSemanal';
import ChipSelector from '../ChipSelector';
import Typography from '../Typography';

import { actionTypes, createInitialState, riegoReducer } from './reducer';
import { setParametros } from '../../ducks/tablero';
import { obtenerRangoFechas } from '../../utilidades/functions';

import endpoints, { CAPTURA_IRRIGACIONES } from '../../configuraciones/endpoints';
import axios from '../../configuraciones/axios';

import TipoSuelo from '../../constantes/tipoSuelo';

import styles, { StyledTableCell, StyledTableRow, Subtitle } from './styles';

const mapearCabeceros = (header) => {
  let propiedades = [];
  let nuevosHeaders = [
    ...new Set(header.map(({ agrupador }) => agrupador?.id).filter((v) => v)),
  ];
  nuevosHeaders = nuevosHeaders.map((nuevoHeader) => {
    const parametro = header.find(
      ({ agrupador }) => agrupador?.id === nuevoHeader,
    );
    const irrigaciones = header.filter(
      ({ agrupadorID }) => nuevoHeader === agrupadorID,
    );
    propiedades = [...propiedades, ...irrigaciones.map(({ id }) => id)];
    return {
      ...parametro.agrupador,
      irrigaciones,
      agrupador: true,
      longitud: irrigaciones.length,
    };
  });

  let headersParametros = [
    ...new Set(header.filter((v) => !v.agrupador).map((v) => v.id)),
  ];
  propiedades = [...propiedades, ...headersParametros];
  headersParametros = headersParametros.map((v) => {
    const irrigacion = header.find(({ id }) => id === v);
    return {
      id: irrigacion.id,
      nombre: irrigacion.nombre,
      irrigaciones: [
        {
          id: irrigacion.id,
          nombre: irrigacion.nombre,
        },
      ],
      agrupador: false,
      longitud: 0,
    };
  });
  nuevosHeaders = [
    { nombre: 'Periodo', longitud: 0 },
    ...nuevosHeaders,
    ...headersParametros,
  ];

  return { nuevosHeaders, propiedades };
};

const SemanasRiego = ({ naveID }) => {
	const classes = styles();
	const dispatch = useDispatch();
	const {
    legend,
    parametros,
    temporada = { fechaInicio: '', fechaFin: '' },
    sitioID,
    semana
  } = useSelector(store => store.tablero);
	const { valvulas = [], cantidadPeriodosRiegos = 0, tipoSueloID } = useSelector(store => store.nave);
	const isMounted = useRef(false);
	const periodosRef = useRef([]);

	const { matutinos, vespertinos } = parametros;
	
  const [state, localDispatch] = useReducer(riegoReducer, { semana }, createInitialState);
  const {
    fecha,
    headerTable,
    irrigaciones,
    irrigacionSemanal,
    parametrosIrrigacion,
    periodos,
    propiedadesRegistros,
    semanaIrrigacion,
    periodoID,
    valvulaID,
  } = state;

  const listaPeriodosNave = useMemo(() => Array.from({ length: cantidadPeriodosRiegos }, (_, i) => ({ id: i + 1, nombre: i + 1 })), [cantidadPeriodosRiegos])

	const promediar = useCallback((irrigacionID, capturas) => {
		const capturasIrrigacion = capturas.filter(
			(captura) => captura.irrigacionID === irrigacionID,
		);
		if (capturasIrrigacion.length === 0) return '--';
		const totalCapturas = capturasIrrigacion.map(({ valor }) => valor);
		const promedio =
			capturasIrrigacion
				.map(({ valor }) => parseFloat(valor))
				.reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || ''), 0) /
			totalCapturas.length;
		return promedio.toFixed(2);
	}, []);

 	const consultarDatosIniciales = useCallback(async () => {
		try {
			if (!sitioID) {
        return;
      }

			const [irrigaciones = [], irrigacionSemanal] = await Promise.all([
				axios(endpoints.obtenerIrrigacionesTipoSuelo(naveID)),
				axios(endpoints.obtenerIrrigacionesSemanales(naveID), { params: { sitioID } }),
			]);

      const { nuevosHeaders, propiedades } = mapearCabeceros(irrigaciones);

      localDispatch({
        type: actionTypes.SET_INITIAL_DATA,
        irrigacionSemanal,
        headerTable: nuevosHeaders,
        propiedadesRegistros: propiedades,
      });
		} catch	{ }
	}, [naveID, sitioID]);

	const obtenerParametros = useCallback(() => {
		if (!sitioID) return;
		const { data, agrupador } = parametrosIrrigacion;
		const semanas = obtenerRangoFechas(temporada.fechaInicio, temporada.fechaFin, 'W-YYYY', true);
		let matutinos = [];
		let vespertinos = [];
		let dataSets = [];
		let agrupadores = [];
		let parametros = [];

		if (agrupador) {
			agrupadores = irrigacionSemanal?.filter(
				(irrigacion) => irrigacion.agrupador === data?.agrupador?.nombre
          && (!valvulaID || irrigacion.valvulaID === valvulaID)
          && (!periodoID || irrigacion.periodo === periodoID)
          && (!fecha || moment(irrigacion.fecha).format('YYYY-MM-DD') === fecha)
			);
			parametros = headerTable?.filter(
				({ nombre }) => nombre === data?.agrupador?.nombre,
			);
		} else {
			agrupadores = irrigacionSemanal?.filter(
				(irrigacion) => irrigacion.parametro === data?.nombre
          && (!valvulaID || irrigacion.valvulaID === valvulaID)
          && (!periodoID || irrigacion.periodo === periodoID)
          && (!fecha || moment(irrigacion.fecha).format('YYYY-MM-DD') === fecha)
			);
			parametros = headerTable?.filter(({ nombre }) => nombre === data?.nombre);
		}

		semanas?.forEach((semana) => {
			parametros[0]?.irrigaciones.forEach(({ nombre }) => {
				const totalMatutinos = agrupadores
					.filter(
						(captura) =>
							captura.parametro === nombre &&
							captura.estadoHorario === 'Matutino' &&
							captura.semana === semana,
					)
					.map(({ valor }) => valor);
				const totalVespertinos = agrupadores
					.filter(
						(captura) =>
							captura.parametro === nombre &&
							captura.estadoHorario === 'Vespertino' &&
							captura.semana === semana,
					)
					.map(({ valor }) => valor);
				const capturasMatutinas =
					agrupadores
						.filter(
							(captura) =>
								captura.parametro === nombre &&
								captura.estadoHorario === 'Matutino' &&
								captura.semana === semana,
						)
						.map(({ valor }) => parseFloat(valor))
						.reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || ''), 0) /
					totalMatutinos.length;
				const capturasVespertinas =
					agrupadores
						.filter(
							(captura) =>
								captura.parametro === nombre &&
								captura.estadoHorario === 'Vespertino' &&
								captura.semana === semana,
						)
						.map(({ valor }) => parseFloat(valor))
						.reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || ''), 0) /
					totalVespertinos.length;

				dataSets.push({
					capturasMatutinas: capturasMatutinas.toFixed(2),
					capturasVespertinas: capturasVespertinas.toFixed(2),
					nombre,
					semana,
				});
			});
		});

    parametros[0]?.irrigaciones.forEach(({ nombre }) => {
      let matutino = [];
      let vespertino = [];
      const capturas = dataSets.filter((captura) => captura.nombre === nombre);
      capturas.forEach((captura) => {
        if (captura.capturasMatutinas) {
          matutino.push(
            captura.capturasMatutinas === 'NaN' ? 0 : captura.capturasMatutinas,
          );
        }
        if (captura.capturasVespertinas) {
          vespertino.push(
            captura.capturasVespertinas === 'NaN' ? 0 : captura.capturasVespertinas,
          );
        }
      });

      const dataSetsMatutino = {
        label: nombre,
        data: matutino,
        hidden: nombre === data?.nombre ? false : true,
        pointStyle: 'circle',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
      };
      const dataSetsVespertino = {
        label: nombre,
        data: vespertino,
        hidden: nombre === data?.nombre ? false : true,
        pointStyle: 'circle',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
      };
      matutinos.push(dataSetsMatutino);
      vespertinos.push(dataSetsVespertino);
    });

		dispatch(setParametros({ matutinos, vespertinos }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parametrosIrrigacion, fecha, valvulaID, periodoID]);

	const establecerSemana = useCallback((semana) => {
    localDispatch({ type: actionTypes.SET_SEMANA, semana })
	}, []);

	const establecerFecha = useCallback((fecha) => {
		localDispatch({ type: actionTypes.SET_FECHA, fecha })
	}, []);

  const filtrarPorValvula = useCallback((valvula) => {
    const esMismo = valvula && valvulaID === valvula.id;

    const periodosFiltrados = periodosRef.current.filter(registro => {
      return !periodoID || registro.periodo === periodoID;
    });
    
    const periodos = periodosFiltrados.map(periodo => {
      const capturasFiltradas = periodo.capturas.filter(captura => {
        return !valvula?.id || captura.valvulaID === valvula.id;
      });
    
      return {
        ...periodo,
        capturas: capturasFiltradas
      };
    });

    localDispatch({
      type: actionTypes.FILTRAR_POR_VALVULA,
      periodos,
      valvulaID: (valvula && !esMismo) ? valvula.id : null
    });

    obtenerParametros();
  }, [obtenerParametros, periodoID, valvulaID])

  const filtrarPorPeriodo = useCallback((periodo) => {
    const esMismo = periodo && periodoID === periodo.id;

    const periodosFiltrados = periodosRef.current.filter(registro => {
      return !periodo?.id || registro.periodo === periodo.id;
    });
    
    const periodos = periodosFiltrados.map(periodo => {
      const capturasFiltradas = periodo.capturas.filter(captura => {
        return !valvulaID || captura.valvulaID === valvulaID;
      });
    
      return {
        ...periodo,
        capturas: capturasFiltradas
      };
    });

    localDispatch({
      type: actionTypes.FILTRAR_POR_PERIODO,
      periodos,
      periodoID: (periodo && !esMismo) ? periodo.id : null
    });

    obtenerParametros();
  }, [obtenerParametros, periodoID, valvulaID]);

	useEffect(() => {
		isMounted.current = true;
		consultarDatosIniciales();

		return () => {
			isMounted.current = false;
		};
	}, [consultarDatosIniciales]);

	useEffect(() => {
		const { id } = legend;
		const parametros = {
			matutinos: matutinos.map((elemento, index) => {
				return {
					...elemento,
					hidden: index === id ? !elemento.hidden : elemento.hidden,
				};
			}),
			vespertinos: vespertinos.map((elemento, index) => ({
				...elemento,
				hidden: index === id ? !elemento.hidden : elemento.hidden,
			})),
		};

		dispatch(setParametros(parametros));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [legend]);

	useEffect(() => {
		const periodosMapeados = Array
      .from(new Set(irrigaciones.map(({ periodo }) => periodo)))
      .map((p) => ({
        periodo: p,
        capturas: irrigaciones.filter(({ periodo }) => periodo === p),
      }));

    periodosRef.current = periodosMapeados;
    localDispatch({ type: actionTypes.SET_PERIODOS, periodos: periodosMapeados });

    return () => {
      periodosRef.current = [];
    }
	}, [irrigaciones]);

	useEffect(() => {
		if (parametrosIrrigacion) {
			obtenerParametros();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [obtenerParametros]);

	useEffect(() => {
		if (semana) {
      localDispatch({ type: actionTypes.SET_SEMANA, semana });
    }
	}, [semana]);

	useEffect(() => {
		if (!sitioID) {
      return;
    }

		const semanaActual = moment(semanaIrrigacion, 'W (YYYY)');
		axios(endpoints.base.url(CAPTURA_IRRIGACIONES, naveID), {
			params: {
				sitioID,
        soloDia: fecha ? 1 : 0,
				fecha: fecha
					? fecha
					: semanaActual.startOf('isoWeek').format('YYYY-MM-DD'),
			}
		})
			.then((irrigaciones) => {
        localDispatch({ type: actionTypes.SET_IRRIGACIONES, irrigaciones })
			});
    
    return () => {
      localDispatch({ type: actionTypes.RESTABLECER_FILTROS });
    }
	}, [naveID, sitioID, fecha, semanaIrrigacion]);

	return (
		<>
      {/* Calendario */}
			<Paper className={classes.calendario}>
				<Grid container className={classes.root}>
					<Grid item xs={12}>
            <CalendarioSemanal
              className={classes.calendarioItem}
							onSelectDia={establecerFecha}
							onChangeSemana={establecerSemana}
							semana={semanaIrrigacion}
              temporada={{
								fechaInicio: temporada ? temporada.fechaInicio : '',
								fechaFin: temporada ? temporada.fechaFin : '',
              }}
						/>
					</Grid>
				</Grid>
			</Paper>

      {/* Filtros */}
      <Box display="flex" padding="10px" gridGap={20} maxWidth="100%" width="fit-content">
        <Box flex="0 1 auto" minWidth={200}>
          <Subtitle variant="subtitle1">Periodos</Subtitle>
          <ChipSelector
            elementos={listaPeriodosNave}
            elementoID={periodoID}
            labels={{ listaVacia: 'No hay periodos disponibles.' }}
            onClick={filtrarPorPeriodo}
          />
        </Box>
        {tipoSueloID === TipoSuelo.HIDROPONIA && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box flex="0 1 auto" minWidth={200}>
              <Subtitle variant="subtitle1">Válvulas</Subtitle>
              <ChipSelector
                elementos={valvulas}
                elementoID={valvulaID}
                onClick={filtrarPorValvula}
              />
            </Box>
          </>
        )}
      </Box>

      {/* DataGrid */}
			<Paper className={classes.tabla}>
				<TableContainer component={Paper}>
					<Table
						className={classes.table}
						size="small"
						aria-label="a dense table"
					>
						<TableHead>
							<StyledTableRow>
								{headerTable.map((option, index) => {
									if (index === 0) {
										return (
											<StyledTableCell
												rowSpan={2}
												className={classes.header}
												key={index}
											>
												<Typography
													className={classes.title}
													aling="center"
													display="block"
												>
													{option.nombre}
												</Typography>
											</StyledTableCell>
										);
									}
									if (option.longitud === 0 || undefined) {
										return (
											<StyledTableCell
												rowSpan={2}
												className={classes.header}
												key={index}
												onClick={() => {
													localDispatch({
                            type: actionTypes.SET_IRRIGACION_A_FILTRAR,
														parametros: option,
														agrupador: false,
													});
												}}
												style={{ cursor: 'pointer' }}
											>
												<Typography
													className={classes.title}
													aling="center"
													display="block"
												>
													{option.nombre}
												</Typography>
											</StyledTableCell>
										);
									} else {
										return (
											<StyledTableCell
												colSpan={option.longitud}
												className={classes.header}
												key={index}
											>
												<Typography
													className={classes.title}
													aling="center"
													display="block"
												>
													{option.nombre}
												</Typography>
											</StyledTableCell>
										);
									}
								})}
							</StyledTableRow>
							<StyledTableRow>
								{headerTable.map(
									(group) =>
										group.longitud > 0 &&
										group.irrigaciones?.map((option, index) => (
											<StyledTableCell
												align="center"
												className={classes.header}
												key={index}
												onClick={() => {
                          localDispatch({
                            type: actionTypes.SET_IRRIGACION_A_FILTRAR,
														parametros: option,
														agrupador: true,
													});
												}}
												style={{ cursor: 'pointer' }}
											>
												{option.nombre}
											</StyledTableCell>
										)),
								)}
							</StyledTableRow>
						</TableHead>
						<TableBody>
							{periodos.map((row, index) => (
								<StyledTableRow key={index}>
									<StyledTableCell align="center">
										{row.periodo}
									</StyledTableCell>
									{propiedadesRegistros.map((irrigacionID, index) => (
										<StyledTableCell align="center" key={index}>
											{promediar(irrigacionID, row.capturas, row.periodo)}
										</StyledTableCell>
									))}
								</StyledTableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</>
	);
};

SemanasRiego.propTypes = {
	/** Identificador de la Nave */
	naveID: propTypes.string.isRequired,
};

export default React.memo(SemanasRiego, (prevProps, nextProps) => {
	return prevProps.naveID === nextProps.naveID;
});
